import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp, 
  query, 
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';

export interface VoucherRedemption {
  id?: string;
  userId: string;
  userEmail?: string;
  orderTitle?: string;
  orderAmount?: number;
  discountApplied: number;
  redeemedAt?: any;
}

export interface EscrowVoucher {
  id: string;
  code: string;
  discountType: 'fixed' | 'percentage';
  discountAmount: number; // in Naira (e.g. 1000) or percentage (e.g. 50)
  minOrderAmount: number; // minimum item price to qualify (e.g. 0)
  maxUsageCount: number; // 0 = unlimited total uses
  usedCount: number; // total times redeemed
  
  // Per-user usage configuration:
  usagePerUser: 'once' | 'multiple'; // 'once' = 1 time per user; 'multiple' = multiple times
  maxPerUser?: number; // if multiple: max times per user (e.g. 3, or 0 for unlimited)
  usedBy?: Record<string, number>; // map of userId -> times used

  // Schedule & Active Duration:
  startsAt?: string | null; // ISO string 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm'
  expiresAt?: string | null; // ISO string 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm'
  isActive: boolean; // Manual toggle (Active vs Paused/Disabled)

  description: string;
  applicableTo: 'escrow_fee';
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
}

export type VoucherStatusType = 'active' | 'scheduled' | 'expired' | 'depleted' | 'disabled';

/**
 * Computes live operational status for a voucher based on schedule, limit, and active toggle.
 */
export const getVoucherStatus = (voucher: EscrowVoucher): {
  status: VoucherStatusType;
  label: string;
  color: string;
  bg: string;
  border: string;
} => {
  if (!voucher.isActive) {
    return {
      status: 'disabled',
      label: 'Disabled / Paused',
      color: 'text-slate-400',
      bg: 'bg-slate-500/15',
      border: 'border-slate-500/30',
    };
  }

  const now = new Date();

  // Check scheduled start date
  if (voucher.startsAt) {
    const startDate = new Date(voucher.startsAt.includes('T') ? voucher.startsAt : `${voucher.startsAt}T00:00:00`);
    if (now < startDate) {
      return {
        status: 'scheduled',
        label: `Scheduled (Starts ${voucher.startsAt})`,
        color: 'text-sky-400',
        bg: 'bg-sky-500/15',
        border: 'border-sky-500/30',
      };
    }
  }

  // Check expiry date
  if (voucher.expiresAt) {
    const expiryDate = new Date(voucher.expiresAt.includes('T') ? voucher.expiresAt : `${voucher.expiresAt}T23:59:59`);
    if (now > expiryDate) {
      return {
        status: 'expired',
        label: `Expired (${voucher.expiresAt})`,
        color: 'text-amber-400',
        bg: 'bg-amber-500/15',
        border: 'border-amber-500/30',
      };
    }
  }

  // Check usage limit depletion
  if (voucher.maxUsageCount > 0 && voucher.usedCount >= voucher.maxUsageCount) {
    return {
      status: 'depleted',
      label: 'Limit Reached (Depleted)',
      color: 'text-rose-400',
      bg: 'bg-rose-500/15',
      border: 'border-rose-500/30',
    };
  }

  return {
    status: 'active',
    label: 'Active & Live',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/15',
    border: 'border-emerald-500/30',
  };
};

export const DEFAULT_VOUCHERS: Omit<EscrowVoucher, 'id'>[] = [
  {
    code: 'WELCOME-REUSEIT-2026',
    discountType: 'fixed',
    discountAmount: 1000,
    minOrderAmount: 5000,
    maxUsageCount: 5000,
    usedCount: 142,
    usagePerUser: 'once',
    maxPerUser: 1,
    startsAt: '2026-01-01',
    description: 'Welcome perk: ₦1,000 Escrow Protection Fee credit for new buyers and traders.',
    applicableTo: 'escrow_fee',
    isActive: true,
    expiresAt: '2026-12-31',
    createdBy: 'superadmin@reuseit.shop',
  },
  {
    code: 'ECOHERO50',
    discountType: 'percentage',
    discountAmount: 50,
    minOrderAmount: 10000,
    maxUsageCount: 1000,
    usedCount: 88,
    usagePerUser: 'multiple',
    maxPerUser: 3,
    startsAt: '2026-02-01',
    description: '50% off escrow fee on pre-loved solar gadgets, refurbished laptops & tech.',
    applicableTo: 'escrow_fee',
    isActive: true,
    expiresAt: '2026-11-30',
    createdBy: 'superadmin@reuseit.shop',
  },
  {
    code: 'ZEROESCROW-LAGOS',
    discountType: 'percentage',
    discountAmount: 100,
    minOrderAmount: 20000,
    maxUsageCount: 200,
    usedCount: 54,
    usagePerUser: 'once',
    maxPerUser: 1,
    startsAt: '2026-03-01',
    description: '100% Free escrow protection on verified circular electronics orders in Lagos.',
    applicableTo: 'escrow_fee',
    isActive: true,
    expiresAt: '2026-10-31',
    createdBy: 'superadmin@reuseit.shop',
  },
];

/**
 * Fetches all escrow fee credit vouchers from Firestore.
 */
export const getVouchers = async (): Promise<EscrowVoucher[]> => {
  try {
    const vouchersCol = collection(db, 'vouchers');
    const snapshot = await getDocs(query(vouchersCol, orderBy('createdAt', 'desc')));

    if (snapshot.empty) {
      // Seed defaults if empty
      const seeded: EscrowVoucher[] = [];
      for (const v of DEFAULT_VOUCHERS) {
        const vRef = doc(db, 'vouchers', v.code);
        await setDoc(vRef, {
          ...v,
          createdAt: serverTimestamp(),
        });
        seeded.push({ id: v.code, ...v });
      }
      return seeded;
    }

    return snapshot.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<EscrowVoucher, 'id'>),
    }));
  } catch (err) {
    console.warn('Could not fetch vouchers from Firestore, using fallback:', err);
    return DEFAULT_VOUCHERS.map((v) => ({ id: v.code, ...v }));
  }
};

/**
 * Fetches detailed customer redemptions for a specific voucher.
 */
export const getVoucherRedemptions = async (code: string): Promise<VoucherRedemption[]> => {
  try {
    const redemptionsCol = collection(db, 'vouchers', code.toUpperCase(), 'redemptions');
    const snapshot = await getDocs(query(redemptionsCol, orderBy('redeemedAt', 'desc'), limit(50)));
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...(d.data() as Omit<VoucherRedemption, 'id'>),
    }));
  } catch (err) {
    console.warn(`Could not load redemptions for voucher ${code}:`, err);
    return [];
  }
};

/**
 * Creates or updates an escrow fee credit voucher in Firestore.
 */
export const saveVoucher = async (
  voucher: Omit<EscrowVoucher, 'id' | 'createdAt' | 'usedCount'> & { usedCount?: number },
  creatorEmail: string = 'superadmin@reuseit.shop'
): Promise<void> => {
  const cleanCode = voucher.code.trim().toUpperCase();
  const vRef = doc(db, 'vouchers', cleanCode);

  await setDoc(
    vRef,
    {
      ...voucher,
      code: cleanCode,
      applicableTo: 'escrow_fee',
      usagePerUser: voucher.usagePerUser || 'once',
      maxPerUser: voucher.maxPerUser || (voucher.usagePerUser === 'once' ? 1 : 0),
      startsAt: voucher.startsAt || null,
      expiresAt: voucher.expiresAt || null,
      usedCount: voucher.usedCount || 0,
      createdBy: creatorEmail,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

/**
 * Updates editable fields of an existing voucher.
 */
export const updateVoucher = async (
  code: string,
  updates: Partial<EscrowVoucher>
): Promise<void> => {
  const vRef = doc(db, 'vouchers', code.toUpperCase());
  await updateDoc(vRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Toggles an existing voucher active/disabled status.
 */
export const toggleVoucherActive = async (code: string, isActive: boolean): Promise<void> => {
  const vRef = doc(db, 'vouchers', code.toUpperCase());
  await updateDoc(vRef, { 
    isActive,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Deletes a voucher from Firestore.
 */
export const deleteVoucher = async (code: string): Promise<void> => {
  const vRef = doc(db, 'vouchers', code.toUpperCase());
  await deleteDoc(vRef);
};
